import { useGetRenewVisaDocumentList } from "@/api/services/main/renew-visa";
import { Pagination } from "@/components/elements/Pagination";
import { Tab } from "@/components/elements/Tab";
import { Table } from "@/components/elements/Table";
import { TopPage } from "@/components/elements/TopPage";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { CreateRenewVisaDocumentGroupBtn } from "@/features/renew-visa/components/CreateRenewVisaDocumentGroupBtn";
import { ExpandRenewVisaListBtn } from "@/features/renew-visa/components/ExpandRenewVisaListBtn";
import { RENEW_VISA_FILTER, TRenewVisaListWithFilter } from "@/features/renew-visa/constants/renewVisaFilter";
import { RENEW_VISA_LIST_TAB, RENEW_VISA_LIST_TAB_LABEL, TRenewVisaListTab } from "@/features/renew-visa/constants/renewVisaListTab";
import { useGetRenewVisaCompleteAndWithdrawnAndRejectedQuantity } from "@/features/renew-visa/hook/useGetRenewVisaCompleteAndWithdrawnAndRejectedQuantity";
import { useGetRenewVisaNotCreatedAndInProgressQuantity } from "@/features/renew-visa/hook/useGetRenewVisaNotCreatedAndInProgressQuantity";
import { useGetRenewVisaUnderReviewAndApprovedQuantity } from "@/features/renew-visa/hook/useGetRenewVisaUnderReviewAndApprovedQuantity";
import { useRenewVisaSelectionTable } from "@/features/renew-visa/hook/useRenewVisaSelectionTable";
import { usePagination } from "@/hooks/usePagination";
import { useSorting } from "@/hooks/useSorting";
import { divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { TApiSortsReq } from "@/types/api";
import { Box, Stack, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
export const RenewVisaListPage = () => {
  const pagReq = usePagination();
  const { sorts } = useSorting();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTab = searchParams.get("tab");
  const [tab, setTab] = useState<TRenewVisaListTab>(searchTab ? searchTab : RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS);
  const [filter, setFilter] = useState<TRenewVisaListWithFilter>(RENEW_VISA_FILTER.NOT_CREATED_AND_IN_PROGRESS);

  const { data: employeeRenewVisaDocumentList, isLoading } = useGetRenewVisaDocumentList({
    page: Number(pagReq.page || START_PAGE),
    perPage: Number(pagReq.perPage || DEFAULT_PER_PAGE),
    sorts: sorts?.length > 0 ? (sorts as TApiSortsReq[]) : filter.sorts,
    status: filter.status,
  });

  useEffect(() => {
    if (tab === RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS) {
      setFilter(RENEW_VISA_FILTER.NOT_CREATED_AND_IN_PROGRESS);
    }
    if (tab === RENEW_VISA_LIST_TAB.UNDER_REVIEW_AND_APPROVED) {
      setFilter(RENEW_VISA_FILTER.UNDER_REVIEW_AND_APPROVED);
    }
    if (tab === RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED) {
      setFilter(RENEW_VISA_FILTER.COMPLETE_AND_WITHDRAWN_AND_REJECTED);
    }
  }, [tab]);

  const { quantity: notCreatedAndInProgessQuantity } = useGetRenewVisaNotCreatedAndInProgressQuantity();
  const { quantity: underReviewAndApprovedQuantity } = useGetRenewVisaUnderReviewAndApprovedQuantity();
  const { quantity: completeAndWithDrawnAndRejectQuantity } = useGetRenewVisaCompleteAndWithdrawnAndRejectedQuantity();

  const { table, rowSelections: selectedRenewVisaDocuments } = useRenewVisaSelectionTable({
    rows: employeeRenewVisaDocumentList?.data ?? [],
  });

  return (
    <>
      <TopPage title="在留期間更新" enablePageBack={false} />
      <RenewVisaListWrapper>
        <Tab
          value={tab}
          onChange={(_, tab) => {
            setTab(tab.toString());
            const updatedParams = new URLSearchParams(searchParams);
            if (tab === RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS) {
              updatedParams.set("tab", RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS);
            }
            if (tab === RENEW_VISA_LIST_TAB.UNDER_REVIEW_AND_APPROVED) {
              updatedParams.set("tab", RENEW_VISA_LIST_TAB.UNDER_REVIEW_AND_APPROVED);
            }
            if (tab === RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED) {
              updatedParams.set("tab", RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED);
            }
            updatedParams.set("page", START_PAGE.toString());
            setSearchParams(updatedParams);
          }}
          options={[
            {
              label: `${RENEW_VISA_LIST_TAB_LABEL[RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS]} (${notCreatedAndInProgessQuantity}) `,
              value: RENEW_VISA_LIST_TAB.NOT_CREATED_AND_IN_PROGRESS,
            },
            {
              label: `${RENEW_VISA_LIST_TAB_LABEL[RENEW_VISA_LIST_TAB.UNDER_REVIEW_AND_APPROVED]} (${underReviewAndApprovedQuantity}) `,
              value: RENEW_VISA_LIST_TAB.UNDER_REVIEW_AND_APPROVED,
            },
            {
              label: `${RENEW_VISA_LIST_TAB_LABEL[RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED]} (${completeAndWithDrawnAndRejectQuantity}) `,
              value: RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED,
            },
          ]}
        >
          <Box padding={3} border={`1px solid ${divider.middle}`} sx={{ borderBottomLeftRadius: rounded.sm, borderBottomRightRadius: rounded.sm }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack flexDirection="row" gap={2}>
                <Pagination totalPage={employeeRenewVisaDocumentList?.pagination.totalPage} />
                <ExpandRenewVisaListBtn filter={filter} />
              </Stack>
              <CreateRenewVisaDocumentGroupBtn selectedRenewVisaDocuments={selectedRenewVisaDocuments} />
            </Stack>
            <Table table={table} isLoading={isLoading} maxHeight="60vh" />
          </Box>
        </Tab>
      </RenewVisaListWrapper>
    </>
  );
};

const RenewVisaListWrapper = styled(Stack)`
  background-color: #ffffff;
  border-radius: 8px;

  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: transparent;
      &::after {
        background-color: transparent;
      }
    }
  }
`;
